import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Utensils,
  Loader2,
  Flame,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Users
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Empty meal template
const emptyMeal = {
  name: "",
  time: "",
  option_a: "",
  option_b: "",
  notes: ""
};

// Default 5-meal structure
const defaultMeals = [
  { name: "Desayuno", time: "08:00", option_a: "", option_b: "", notes: "" },
  { name: "Media mañana", time: "11:00", option_a: "", option_b: "", notes: "" },
  { name: "Comida", time: "14:00", option_a: "", option_b: "", notes: "" },
  { name: "Merienda", time: "17:00", option_a: "", option_b: "", notes: "" },
  { name: "Cena", time: "20:00", option_a: "", option_b: "", notes: "" },
];

const AdminDiets = () => {
  const { getAuthHeaders } = useAuth();
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDiet, setEditingDiet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedDiet, setExpandedDiet] = useState(null);
  const [expandedMeals, setExpandedMeals] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    calories_target: "",
    protein_target: "",
    carbs_target: "",
    fat_target: "",
    meals: [...defaultMeals]
  });

  useEffect(() => {
    fetchDiets();
  }, []);

  const fetchDiets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/diets`, {
        headers: getAuthHeaders(),
      });
      setDiets(response.data);
    } catch (error) {
      console.error("Error fetching diets:", error);
      toast.error("Error al cargar dietas");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      calories_target: "",
      protein_target: "",
      carbs_target: "",
      fat_target: "",
      meals: [...defaultMeals.map(m => ({ ...m }))]
    });
    setEditingDiet(null);
    setExpandedMeals({});
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (diet) => {
    setEditingDiet(diet);
    setFormData({
      name: diet.name || "",
      description: diet.description || "",
      calories_target: diet.calories_target?.toString() || "",
      protein_target: diet.protein_target?.toString() || "",
      carbs_target: diet.carbs_target?.toString() || "",
      fat_target: diet.fat_target?.toString() || "",
      meals: diet.meals?.length > 0 ? [...diet.meals.map(m => ({ ...m }))] : [...defaultMeals.map(m => ({ ...m }))]
    });
    setDialogOpen(true);
  };

  const handleDuplicate = (diet) => {
    setEditingDiet(null);
    setFormData({
      name: `${diet.name} (copia)`,
      description: diet.description || "",
      calories_target: diet.calories_target?.toString() || "",
      protein_target: diet.protein_target?.toString() || "",
      carbs_target: diet.carbs_target?.toString() || "",
      fat_target: diet.fat_target?.toString() || "",
      meals: diet.meals?.length > 0 ? [...diet.meals.map(m => ({ ...m }))] : [...defaultMeals.map(m => ({ ...m }))]
    });
    setDialogOpen(true);
    toast.info("Creando copia de la dieta");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!formData.calories_target || parseInt(formData.calories_target) <= 0) {
      toast.error("Las calorías deben ser un número positivo");
      return;
    }
    if (!formData.protein_target || parseInt(formData.protein_target) <= 0) {
      toast.error("La proteína debe ser un número positivo");
      return;
    }

    // Filter out empty meals and validate at least one meal
    const validMeals = formData.meals.filter(m => m.name.trim() && m.option_a.trim());
    if (validMeals.length === 0) {
      toast.error("Debe haber al menos una comida con nombre y opción A");
      return;
    }

    setSaving(true);

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      calories_target: parseInt(formData.calories_target),
      protein_target: parseInt(formData.protein_target),
      carbs_target: formData.carbs_target ? parseInt(formData.carbs_target) : null,
      fat_target: formData.fat_target ? parseInt(formData.fat_target) : null,
      meals: validMeals.map(m => ({
        name: m.name.trim(),
        time: m.time.trim() || null,
        option_a: m.option_a.trim(),
        option_b: m.option_b?.trim() || null,
        notes: m.notes?.trim() || null
      }))
    };

    try {
      if (editingDiet) {
        await axios.put(
          `${API_URL}/api/admin/diets/${editingDiet.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Dieta actualizada");
      } else {
        await axios.post(
          `${API_URL}/api/admin/diets`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Dieta creada");
      }
      fetchDiets();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving diet:", error);
      toast.error(error.response?.data?.detail || "Error al guardar dieta");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dietId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta dieta?")) return;

    setDeleting(dietId);
    try {
      await axios.delete(`${API_URL}/api/admin/diets/${dietId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Dieta eliminada");
      fetchDiets();
    } catch (error) {
      console.error("Error deleting diet:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar dieta");
    } finally {
      setDeleting(null);
    }
  };

  const updateMeal = (index, field, value) => {
    const newMeals = [...formData.meals];
    newMeals[index] = { ...newMeals[index], [field]: value };
    setFormData({ ...formData, meals: newMeals });
  };

  const addMeal = () => {
    setFormData({
      ...formData,
      meals: [...formData.meals, { ...emptyMeal }]
    });
  };

  const removeMeal = (index) => {
    if (formData.meals.length <= 1) {
      toast.error("Debe haber al menos una comida");
      return;
    }
    const newMeals = formData.meals.filter((_, i) => i !== index);
    setFormData({ ...formData, meals: newMeals });
  };

  const toggleMealExpand = (index) => {
    setExpandedMeals(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getMealIcon = (mealName) => {
    const name = mealName?.toLowerCase() || "";
    if (name.includes("desayuno")) return "🌅";
    if (name.includes("mañana") || name.includes("media")) return "🍎";
    if (name.includes("comida") || name.includes("almuerzo")) return "🍽️";
    if (name.includes("merienda")) return "🥤";
    if (name.includes("cena")) return "🌙";
    return "🍴";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-diets">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Dietas</h1>
          <p className="text-muted-foreground mt-1">Gestiona los planes de alimentación</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              onClick={openCreateDialog}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" 
              data-testid="add-diet-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Dieta
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-xl">
                {editingDiet ? "Editar Dieta" : "Nueva Dieta"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Nombre de la dieta *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                    placeholder="Ej: Definición 2000kcal"
                    data-testid="diet-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Descripción (opcional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white resize-none"
                    placeholder="Ej: Plan para fase de corte, alto en proteína"
                    rows={2}
                  />
                </div>
              </div>

              {/* Macros */}
              <div className="space-y-3">
                <Label className="text-white font-semibold">Macros objetivo</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#888]">Calorías *</Label>
                    <Input
                      type="number"
                      value={formData.calories_target}
                      onChange={(e) => setFormData({ ...formData, calories_target: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                      placeholder="2000"
                      data-testid="diet-calories-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#888]">Proteína (g) *</Label>
                    <Input
                      type="number"
                      value={formData.protein_target}
                      onChange={(e) => setFormData({ ...formData, protein_target: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                      placeholder="150"
                      data-testid="diet-protein-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#888]">Carbos (g)</Label>
                    <Input
                      type="number"
                      value={formData.carbs_target}
                      onChange={(e) => setFormData({ ...formData, carbs_target: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                      placeholder="200"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#888]">Grasas (g)</Label>
                    <Input
                      type="number"
                      value={formData.fat_target}
                      onChange={(e) => setFormData({ ...formData, fat_target: e.target.value })}
                      className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                      placeholder="55"
                    />
                  </div>
                </div>
              </div>

              {/* Meals */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white font-semibold">Comidas</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addMeal}
                    className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir comida
                  </Button>
                </div>

                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                  {formData.meals.map((meal, index) => {
                    const isExpanded = expandedMeals[index] !== false; // Default expanded
                    return (
                      <Card key={meal.name ? `meal-${meal.name}-${index}` : `meal-${index}`} className="bg-[#1A1A1A] border-[#2A2A2A]">
                        <CardContent className="p-3">
                          {/* Meal header - always visible */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{getMealIcon(meal.name)}</span>
                            <Input
                              value={meal.name}
                              onChange={(e) => updateMeal(index, "name", e.target.value)}
                              className="bg-[#0B0B0B] border-[#2A2A2A] focus:border-[#D4AF37] text-white h-8 flex-1"
                              placeholder="Nombre (ej: Desayuno)"
                            />
                            <Input
                              value={meal.time}
                              onChange={(e) => updateMeal(index, "time", e.target.value)}
                              className="bg-[#0B0B0B] border-[#2A2A2A] focus:border-[#D4AF37] text-white h-8 w-20"
                              placeholder="08:00"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleMealExpand(index)}
                              className="h-8 w-8 text-[#888] hover:text-white"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeMeal(index)}
                              className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Expanded content */}
                          {isExpanded && (
                            <div className="space-y-2 mt-3 pl-8">
                              <div className="space-y-1">
                                <Label className="text-xs text-green-400">Opción A *</Label>
                                <Textarea
                                  value={meal.option_a}
                                  onChange={(e) => updateMeal(index, "option_a", e.target.value)}
                                  className="bg-[#0B0B0B] border-[#2A2A2A] focus:border-green-500 text-white text-sm resize-none"
                                  placeholder="Ej: 4 claras + 2 huevos + 80g avena (en crudo)"
                                  rows={2}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-blue-400">Opción B (opcional)</Label>
                                <Textarea
                                  value={meal.option_b || ""}
                                  onChange={(e) => updateMeal(index, "option_b", e.target.value)}
                                  className="bg-[#0B0B0B] border-[#2A2A2A] focus:border-blue-500 text-white text-sm resize-none"
                                  placeholder="Alternativa con macros similares"
                                  rows={2}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-[#888]">Notas (opcional)</Label>
                                <Input
                                  value={meal.notes || ""}
                                  onChange={(e) => updateMeal(index, "notes", e.target.value)}
                                  className="bg-[#0B0B0B] border-[#2A2A2A] focus:border-[#D4AF37] text-white text-sm h-8"
                                  placeholder="Ej: Pesos en crudo"
                                />
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-[#2A2A2A]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                  data-testid="save-diet-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : editingDiet ? (
                    "Actualizar Dieta"
                  ) : (
                    "Crear Dieta"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats bar */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] rounded-lg">
          <Utensils className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-white font-semibold">{diets.length}</span>
          <span className="text-[#888]">dietas</span>
        </div>
      </div>

      {/* Diets List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
        </div>
      ) : diets.length === 0 ? (
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-12 text-center">
            <Utensils className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <h3 className="text-white text-lg font-semibold mb-2">No hay dietas</h3>
            <p className="text-[#888] mb-4">Crea tu primera dieta para asignarla a usuarios Premium</p>
            <Button 
              onClick={openCreateDialog}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primera dieta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {diets.map((diet) => {
            const isExpanded = expandedDiet === diet.id;
            return (
              <Card 
                key={diet.id} 
                className={cn(
                  "bg-[#121212] border-[#2A2A2A] transition-all",
                  isExpanded && "border-[#D4AF37]/30"
                )}
              >
                <CardContent className="p-0">
                  {/* Diet header */}
                  <div 
                    className="p-4 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedDiet(isExpanded ? null : diet.id)}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Utensils className="w-6 h-6 text-green-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-lg truncate">{diet.name}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1 text-sm text-orange-400">
                          <Flame className="w-3 h-3" />
                          {diet.calories_target} kcal
                        </span>
                        <span className="text-sm text-[#888]">
                          {diet.protein_target}g proteína
                        </span>
                        <Badge variant="outline" className="border-[#2A2A2A] text-[#888]">
                          {diet.meals?.length || 0} comidas
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(diet); }}
                        className="text-[#888] hover:text-white hover:bg-[#1A1A1A]"
                        title="Duplicar"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); openEditDialog(diet); }}
                        className="text-[#888] hover:text-white hover:bg-[#1A1A1A]"
                        data-testid={`edit-diet-${diet.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(diet.id); }}
                        disabled={deleting === diet.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-diet-${diet.id}`}
                      >
                        {deleting === diet.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                      <div className={cn(
                        "p-2 rounded-lg transition-colors",
                        isExpanded ? "bg-[#D4AF37]/20" : "bg-[#1A1A1A]"
                      )}>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-[#D4AF37]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#666]" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded meals preview */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[#2A2A2A] mt-0 pt-4 space-y-2">
                      {diet.description && (
                        <p className="text-[#888] text-sm mb-3">{diet.description}</p>
                      )}
                      
                      {/* Macros summary */}
                      <div className="flex flex-wrap gap-4 p-3 bg-[#1A1A1A] rounded-lg mb-3">
                        <div className="text-center">
                          <span className="text-lg font-bold text-white">{diet.calories_target}</span>
                          <p className="text-[10px] text-[#888] uppercase">Calorías</p>
                        </div>
                        <div className="text-center">
                          <span className="text-lg font-bold text-white">{diet.protein_target}g</span>
                          <p className="text-[10px] text-[#888] uppercase">Proteína</p>
                        </div>
                        {diet.carbs_target && (
                          <div className="text-center">
                            <span className="text-lg font-bold text-white">{diet.carbs_target}g</span>
                            <p className="text-[10px] text-[#888] uppercase">Carbos</p>
                          </div>
                        )}
                        {diet.fat_target && (
                          <div className="text-center">
                            <span className="text-lg font-bold text-white">{diet.fat_target}g</span>
                            <p className="text-[10px] text-[#888] uppercase">Grasas</p>
                          </div>
                        )}
                      </div>

                      {/* Meals list */}
                      {diet.meals?.map((meal, idx) => (
                        <div key={idx} className="p-3 bg-[#0B0B0B] rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{getMealIcon(meal.name)}</span>
                            <span className="text-white font-medium">{meal.name}</span>
                            {meal.time && (
                              <span className="text-[#666] text-sm flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {meal.time}
                              </span>
                            )}
                          </div>
                          <div className="pl-8 space-y-1">
                            <p className="text-sm">
                              <span className="text-green-400 font-medium">A:</span>{" "}
                              <span className="text-[#ccc]">{meal.option_a}</span>
                            </p>
                            {meal.option_b && (
                              <p className="text-sm">
                                <span className="text-blue-400 font-medium">B:</span>{" "}
                                <span className="text-[#ccc]">{meal.option_b}</span>
                              </p>
                            )}
                            {meal.notes && (
                              <p className="text-xs text-[#888] italic mt-1">{meal.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDiets;
